import { useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  IconAdjustments,
  IconAlertTriangle,
  IconBook2,
  IconCalendarEvent,
  IconCreditCard,
  IconExternalLink,
  IconFlask,
  IconLayoutDashboard,
  IconLogout,
  IconMenu2,
  IconMoon,
  IconMail,
  IconPlugConnected,
  IconShieldLock,
  IconSun,
  IconTopologyStar3,
  IconUsers,
  IconX,
} from '@tabler/icons-react';
import { useAuth, useEvents, useHealth, useTheme } from '../state';
import { formatLimit, formatRelative, pluralize } from '../lib/format';
import { Badge, Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, Separator, TooltipProvider } from './ui';

type NavItem = {
  to: string;
  label: string;
  end: boolean;
  icon: typeof IconLayoutDashboard;
  adminOnly?: boolean;
};

const WORKSPACE_NAV: readonly NavItem[] = [
  { to: '/', label: 'Projects', end: true, icon: IconLayoutDashboard },
  { to: '/suites', label: 'Suites', end: false, icon: IconFlask },
  { to: '/schedules', label: 'Schedules', end: false, icon: IconCalendarEvent },
  { to: '/tunnels', label: 'Tunnels', end: false, icon: IconPlugConnected },
  { to: '/docs', label: 'CLI & Docs', end: false, icon: IconBook2 },
  { to: '/billing', label: 'Billing', end: false, icon: IconCreditCard },
  { to: '/settings', label: 'Settings', end: false, icon: IconAdjustments },
];

const ADMIN_NAV: readonly NavItem[] = [
  { to: '/admin', label: 'Owner overview', end: true, icon: IconShieldLock, adminOnly: true },
  { to: '/admin/llm', label: 'Hosted models', end: false, icon: IconTopologyStar3, adminOnly: true },
  { to: '/admin/customers', label: 'Customers & Quotas', end: false, icon: IconUsers, adminOnly: true },
  { to: '/admin/smtp', label: 'Email & SMTP', end: false, icon: IconMail, adminOnly: true },
];

const NAV: readonly NavItem[] = [...WORKSPACE_NAV, ...ADMIN_NAV];

/**
 * Application frame. Keep live run health visible while giving pages a stable,
 * narrow navigation rail and a responsive menu on compact screens.
 */
export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const isAdmin = auth.user?.role === 'admin';
  const visibleNav = NAV.filter((item) => !item.adminOnly || isAdmin);

  const signOut = () => {
    auth.signOut();
    navigate('/login', { replace: true });
  };

  return (
    <TooltipProvider delayDuration={350}>
      <div className="shell">
        <a className="skip-link" href="#main">Skip to content</a>
        <BillingStatusBanner />
        <Header onSignOut={signOut} navItems={visibleNav} />
        <div className="shell__body">
          <aside className="shell__sidebar">
            <div className="shell__sidebar-section-label">WORKSPACE</div>
            <nav aria-label="Primary navigation">
              <ul className="shell__nav-list">
                {WORKSPACE_NAV.map((item) => <NavigationItem key={item.to} item={item} />)}
              </ul>
            </nav>
            {isAdmin ? (
              <div className="mt-4">
                <div className="shell__sidebar-section-label">CONTROL PLANE</div>
                <nav aria-label="Admin navigation">
                  <ul className="shell__nav-list">
                    {ADMIN_NAV.map((item) => <NavigationItem key={item.to} item={item} />)}
                  </ul>
                </nav>
              </div>
            ) : null}
            <div className="mt-auto pt-4">
              <Separator className="shell__sidebar-separator" />
              <NavLink to="/landing" className="shell__external-link">
                <IconExternalLink size={15} stroke={1.6} aria-hidden="true" />
                <span>Marketing site</span>
              </NavLink>
              <div className="shell__sidebar-foot">
                <span className="shell__foot-mark" aria-hidden="true">J</span>
                <span>Verification, with evidence.</span>
              </div>
            </div>
          </aside>
          <main className="shell__main" id="main">
            <div className="page-frame">{children}</div>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}

function NavigationItem({ item }: { item: NavItem }): JSX.Element {
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        to={item.to}
        end={item.end}
        className={({ isActive }) => `shell__nav-link${isActive ? ' is-active' : ''}`}
      >
        <Icon size={17} stroke={1.6} aria-hidden="true" />
        <span className="truncate">{item.label}</span>
        {item.adminOnly ? (
          <Badge variant="secondary" className="shell__owner-badge ml-auto">
            OWNER
          </Badge>
        ) : null}
      </NavLink>
    </li>
  );
}

function Header({ onSignOut, navItems }: { onSignOut: () => void; navItems: readonly NavItem[] }): JSX.Element {
  const { status: streamStatus, lastEventAt, eventCount } = useEvents();
  const { user, organization } = useAuth();
  const { theme, toggle } = useTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <header className="topbar">
      <div className="topbar__brand">
        <NavLink to="/" className="flex items-center gap-2.5 no-underline">
          <span className="landing__logo-mark !w-7 !h-7 !text-xs !rounded-lg" aria-hidden="true">J</span>
          <div className="topbar__brand-text">
            <span className="brand__name">Justloop</span>
            <span className="brand__tagline">verification for agents that ship</span>
          </div>
        </NavLink>
      </div>

      <div className="topbar__status" aria-label="Live platform status">
        <ServerStatus />
        <QueueStatus />
        <CreditsStatus />
        <StreamStatus status={streamStatus} lastEventAt={lastEventAt} events={eventCount} />
      </div>

      <div className="topbar__actions">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="topbar__theme-button"
          onClick={toggle}
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
          title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
        >
          {theme === 'dark' ? <IconSun size={18} stroke={1.7} /> : <IconMoon size={18} stroke={1.7} />}
        </Button>
        <div className="topbar__identity">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-300 font-semibold text-[10px] flex items-center justify-center shrink-0">
              {user?.email ? user.email.charAt(0).toUpperCase() : 'U'}
            </div>
            <div className="flex flex-col text-right">
              <span className="topbar__org" title={organization?.name}>{organization?.name ?? 'workspace'}</span>
              <span className="topbar__user">{user?.email ?? 'signed out'}</span>
            </div>
          </div>
        </div>
        {user?.role === 'admin' ? <Badge variant="secondary" className="topbar__admin-badge">ADMIN</Badge> : null}
        <DropdownMenu open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="icon" className="topbar__mobile-menu" aria-label="Open navigation menu">
              {mobileNavOpen ? <IconX size={18} stroke={1.7} /> : <IconMenu2 size={18} stroke={1.7} />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="topbar__mobile-menu-content">
            <DropdownMenuLabel>Workspace</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <DropdownMenuItem key={item.to} asChild>
                  <NavLink to={item.to} end={item.end} onClick={() => setMobileNavOpen(false)}>
                    <Icon size={16} stroke={1.7} aria-hidden="true" />
                    <span>{item.label}</span>
                  </NavLink>
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <NavLink to="/landing" onClick={() => setMobileNavOpen(false)}>
                <IconExternalLink size={16} stroke={1.7} aria-hidden="true" />
                <span>Marketing site</span>
              </NavLink>
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onSignOut}>
              <IconLogout size={16} stroke={1.7} aria-hidden="true" />
              <span>Sign out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button type="button" variant="outline" size="sm" className="topbar__signout" onClick={onSignOut}>
          <IconLogout size={16} stroke={1.7} aria-hidden="true" />
          Sign out
        </Button>
      </div>
    </header>
  );
}

function ServerStatus(): JSX.Element {
  const { health, connection } = useHealth();
  if (connection === 'offline') {
    return (
      <Badge variant="outline" className="status status--offline" title="The Justloop server did not answer /v1/health">
        <span className="status__dot" aria-hidden="true" />
        server unreachable
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className={`status ${health?.status === 'ok' ? 'status--ok' : 'status--warn'}`} title={`min client ${health?.minClientVersion ?? '-'}`}>
      <span className="status__dot" aria-hidden="true" />
      {health?.version ? `v${health.version}` : 'checking…'}
      {health?.status === 'degraded' ? ' · degraded' : ''}
    </Badge>
  );
}

function QueueStatus(): JSX.Element {
  const { health } = useHealth();
  const queues = health?.queues;
  if (!queues) return <Badge variant="outline" className="status status--idle">queue unavailable</Badge>;
  return (
    <Badge variant="outline" className="status" title={`concurrency ${queues.concurrency}, ${pluralize(queues.queued, 'job')} waiting`}>
      <span className="status__dot" aria-hidden="true" />
      <span className="num">{queues.running}/{queues.concurrency}</span>
      running
      {queues.queued > 0 ? <span className="status__badge num">{queues.queued} queued</span> : null}
    </Badge>
  );
}

function CreditsStatus(): JSX.Element {
  const { credits, subscription } = useAuth();
  const navigate = useNavigate();
  if (!credits) return <Badge variant="outline" className="status status--idle">credits unavailable</Badge>;
  const ratio = credits.creditsMonthly > 0 ? credits.creditsRemaining / credits.creditsMonthly : 0;
  const isPastDue = subscription?.status === 'past_due' || subscription?.status === 'incomplete';
  const isZero = credits.creditsRemaining <= 0;
  const isCritical = credits.creditsRemaining <= 5 || ratio <= 0.1 || isPastDue || isZero;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={`status topbar__credit-status ${isCritical ? 'status--warn' : 'status--idle'}`}
      onClick={() => navigate('/billing')}
      title={`${credits.creditsRemaining} of ${credits.creditsMonthly} credits left · ${credits.environmentsUsed}/${formatLimit(credits.environmentsAllowed, 'environment')} environments · plan ${credits.plan}${isPastDue ? ` · subscription ${subscription?.status}` : ''} · Click to manage billing & top-up`}
    >
      <span className="status__dot" aria-hidden="true" />
      <span className="num">{formatCredits(credits.creditsRemaining)}</span> credits
    </Button>
  );
}

export function BillingStatusBanner(): JSX.Element | null {
  const { subscription, credits } = useAuth();
  const navigate = useNavigate();

  const isPastDue = subscription?.status === 'past_due';
  const isIncomplete = subscription?.status === 'incomplete';
  const isZeroCredits = credits !== null && credits.creditsRemaining <= 0;

  if (!isPastDue && !isIncomplete && !isZeroCredits) {
    return null;
  }

  let title = 'Billing action required';
  let message = 'New test runs are paused until billing is updated.';

  if (isPastDue) {
    title = 'Subscription Past Due';
    message = 'Your subscription is past due. New test runs are paused until billing is updated.';
  } else if (isIncomplete) {
    title = 'Payment Incomplete';
    message = 'Your subscription payment is incomplete. New test runs are paused until billing is updated.';
  } else if (isZeroCredits) {
    title = 'Credits Depleted';
    message = 'Your credit balance is 0. New test runs are paused until billing is updated or credits are topped up.';
  }

  return (
    <aside
      className="billing-alert-banner"
      role="alert"
      aria-live="assertive"
      aria-label="Billing status alert"
    >
      <div className="billing-alert-banner__content">
        <span className="billing-alert-banner__icon" aria-hidden="true">
          <IconAlertTriangle size={18} stroke={1.8} />
        </span>
        <div className="billing-alert-banner__text">
          <strong className="billing-alert-banner__title">{title}:</strong>{' '}
          <span>{message}</span>
        </div>
      </div>
      <div className="billing-alert-banner__action">
        <Button type="button" variant="destructive" size="sm" onClick={() => navigate('/billing')}>
          Update billing
        </Button>
      </div>
    </aside>
  );
}

function formatCredits(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function StreamStatus({
  status,
  lastEventAt,
  events,
}: {
  status: string;
  lastEventAt: string | null;
  events: number;
}): JSX.Element {
  const tone =
    status === 'open' ? 'ok' : status === 'idle' ? 'idle' : status === 'connecting' ? 'warn' : 'warn';
  const label =
    status === 'open'
      ? 'live'
      : status === 'idle'
        ? 'stream off'
        : status === 'connecting'
          ? 'connecting'
          : status === 'reconnecting'
            ? 'reconnecting'
            : 'stream error';
  return (
    <span
      className={`status status--${tone}`}
      title={
        lastEventAt
          ? `${events} event(s) · last ${formatRelative(lastEventAt)}`
          : 'no events received yet on this connection'
      }
    >
      <span className="status__dot" aria-hidden="true" />
      {label}
    </span>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}): JSX.Element {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle ? <p className="page-header__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </div>
  );
}